import { Autocomplete, TextField as MuiTextField, useTheme } from '@mui/material';

import { ReactComponent as ChevronDownIcon } from '@/assets/icons/chevron-down-icon.svg';

import { FilterFieldContainer, FilterLabel } from '../GeneratikTableFilterPanel.styles';
import { AutocompleteFieldProps, FilterOption } from '../GeneratikTableFilterPanel.types';
import { autocompleteSx, getInputFieldSx } from './AutocompleteFilterField.styles';

const sortByLabel = (options: FilterOption[]) =>
  options.slice().sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));

const AutocompleteFilterField = ({
  field,
  value,
  onChange,
  t,
  disabled,
}: AutocompleteFieldProps) => {
  const theme = useTheme();
  const multiple = field.multiple === true;
  const selectedValues = multiple ? ((value as string[]) ?? []) : [];

  const selectedValue = multiple
    ? (field.options?.filter((opt) => selectedValues.includes(opt.value)) ?? [])
    : (field.options?.find((opt) => opt.value === value) ?? null);

  const availableOptions = multiple
    ? sortByLabel((field.options ?? []).filter((opt) => !selectedValues.includes(opt.value)))
    : sortByLabel(field.options ?? []);

  return (
    <FilterFieldContainer data-testid={`filter-field-${field.id}`}>
      <FilterLabel>{field.label}</FilterLabel>
      <Autocomplete
        data-testid={`filter-field-${field.id}`}
        multiple={multiple}
        options={availableOptions}
        getOptionLabel={(option) => option.label}
        getOptionKey={(option) => option.value}
        value={selectedValue}
        disableCloseOnSelect={multiple}
        disabled={disabled}
        openOnFocus
        popupIcon={<ChevronDownIcon />}
        onChange={(_, newValue) => {
          if (multiple) {
            onChange((newValue as typeof field.options)?.map((v) => v.value) ?? []);
          } else {
            onChange((newValue as FilterOption | null)?.value ?? '');
          }
        }}
        size="small"
        sx={autocompleteSx}
        renderInput={(params) => (
          <MuiTextField
            {...params}
            placeholder={field.placeholder || String(t('Search...'))}
            size="small"
            sx={getInputFieldSx(theme)}
          />
        )}
      />
    </FilterFieldContainer>
  );
};

export default AutocompleteFilterField;
